import { graphqlRequest, graphqlUpload } from "./client";
import { ASSESSMENT_FIELDS, type AssessmentView } from "./assessment";
import {
  CHECK_FIELDS,
  GATE_FIELDS,
  JOB_FIELDS,
  PROFILE_FIELDS,
  type CheckItem,
  type Job,
  type KycStatus,
  type PlatformProfile,
  type RatePackage,
  type StepGate,
} from "./types";

export interface Invoice {
  no: string;
  client: string;
  clientEmail: string;
  dueDays: number;
  items: { description: string; amount?: number | null }[];
  payVia: string;
}

export interface PaidReadyView {
  step: {
    payMethods: string[];
    payConfirmed: boolean;
    invoiceDone: boolean;
    qualityPct?: number | null;
  };
  profiles: PlatformProfile[];
  invoice: Invoice;
  invoiceChecks: CheckItem[];
  invoiceTotal: number;
  assessment: AssessmentView;
  gate: StepGate;
  done: boolean;
}

export interface LaunchPlan {
  name: string;
  trackLabel: string;
  workEnabledAt: string;
  niche: string;
  profiles: PlatformProfile[];
  portfolioUrl: string;
  qualityPct?: number | null;
  packages: RatePackage[];
  payMethods: string[];
  targetJobs: Job[];
  bestBid?: { job: Job; pct: number } | null;
}

const INVOICE_FIELDS = "no client clientEmail dueDays items { description amount } payVia";
const PAID_FIELDS = `step { payMethods payConfirmed invoiceDone qualityPct } profiles { ${PROFILE_FIELDS} }
  invoice { ${INVOICE_FIELDS} } invoiceChecks { ${CHECK_FIELDS} } invoiceTotal assessment { ${ASSESSMENT_FIELDS} } ${GATE_FIELDS} done`;

/** Step 4: Get Paid-Ready. */
export async function fetchPaidReady(): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ getPaidReady: PaidReadyView }>(`{ getPaidReady { ${PAID_FIELDS} } }`)
  ).getPaidReady;
}

/** Save payment methods and the verified-account confirmation. */
export async function savePayment(methods: string[], confirmed: boolean): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ savePayment: PaidReadyView }>(
      `mutation($input: SavePaymentInput!) { savePayment(input: $input) { ${PAID_FIELDS} } }`,
      { variables: { input: { methods, confirmed } } },
    )
  ).savePayment;
}

/** Set the KYC status on a verified platform. */
export async function setKycStatus(platformId: string, status: KycStatus): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ setKycStatus: PaidReadyView }>(
      `mutation($input: SetKycStatusInput!) { setKycStatus(input: $input) { ${PAID_FIELDS} } }`,
      { variables: { input: { platformId, status } } },
    )
  ).setKycStatus;
}

/** Upload a KYC status screenshot. */
export async function uploadKycScreenshot(platformId: string, file: File): Promise<PaidReadyView> {
  return (
    await graphqlUpload<{ uploadKycScreenshot: PaidReadyView }>(
      `mutation($platformId: String!, $file: Upload!) { uploadKycScreenshot(platformId: $platformId, file: $file) { ${PAID_FIELDS} } }`,
      { platformId },
      "file",
      file,
    )
  ).uploadKycScreenshot;
}

/** Save an invoice draft. */
export async function saveInvoice(input: Invoice): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ saveInvoice: PaidReadyView }>(
      `mutation($input: InvoiceInput!) { saveInvoice(input: $input) { ${PAID_FIELDS} } }`,
      { variables: { input } },
    )
  ).saveInvoice;
}

/** Confirm the invoice once its checks pass. */
export async function confirmInvoice(): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ confirmInvoice: PaidReadyView }>(
      `mutation { confirmInvoice { ${PAID_FIELDS} } }`,
    )
  ).confirmInvoice;
}

/** Complete Step 4 and become Work-Enabled. */
export async function completePaidReady(): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ completePaidReady: PaidReadyView }>(
      `mutation { completePaidReady { ${PAID_FIELDS} } }`,
    )
  ).completePaidReady;
}

/** The Launch Plan, available once Work-Enabled. */
export async function fetchLaunchPlan(): Promise<LaunchPlan> {
  return (
    await graphqlRequest<{ getLaunchPlan: LaunchPlan }>(
      `{ getLaunchPlan { name trackLabel workEnabledAt niche portfolioUrl qualityPct payMethods
        profiles { ${PROFILE_FIELDS} } packages { name price days incl } targetJobs { ${JOB_FIELDS} }
        bestBid { pct job { ${JOB_FIELDS} } } } }`,
    )
  ).getLaunchPlan;
}
